import { FeatureFlags } from '../../ts/FeatureFlags';
import { ServiceLocator } from '../../app/ServiceLocator';
import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { Helpers } from '../../ts/helpers/Helpers';
import { ServerService } from '../../ts/services/ServerService';
import { Telemetry } from './Telemetry';

const ONLINE_TOKEN_OFFLINE_PAGE_DICT: {[key: string]: string} = {
  'adminusers2': 'csvguidelines.htm',
  'status1': 'service_viewstatus.htm',
  'settings-adsync1': 'groups_globalsync.htm'
};

const TABLEAU_URL = 'https://www.tableau.com';

function search(searchText: string): void {
  searchText = encodeURIComponent(searchText);

  if(FeatureFlags.OnlineAnalyticsPipelineEnabled) {
    Telemetry.sendEvent({
      type: TelemetryEvents.InfoMenuSearchHelp,
      action: 'enterKeypress',
      context: JSON.stringify({searchText: searchText })
    });
  }
  if (getServerService().offlineHelpEnabled()) {
    // Example: http://localhost/offline_help/en_us/help.htm#search-workbooks
    window.open(offlineHelpRoute('search-') + searchText, '_blank');
  } else {
    // Example: https://www.tableau.com/search/support/workbooks
    window.open(supportUrlPrefix() + '/search/support/' + searchText, '_blank');
  }
}

function getServerHelpUrlByCategory(category?: string): string {
  if (getServerService().offlineHelpEnabled()) {
    return offlineHelpRoute(ONLINE_TOKEN_OFFLINE_PAGE_DICT[category]);
  } else {
    const lang = helpUrlLanguage();
    // Params must be ordered: edition, lang, platform, version

    const query = Helpers.serialize({
      'edition': getServerService().helpEdition(),
      'lang': lang,
      'platform': serverPlatform(),
      'version': externalVersion()
    });
    // Example: https://www.tableau.com/en-us/app/server-views1?edition=server&lang=en-us&platform=windows&version=9.0
    return getHelpUrl('server-', lang, query, category);
  }
}

function getHelpUrlByCategory(category?: string, edition?: string, version?: string): string {
  const lang = helpUrlLanguage();

  // Params must be ordered: edition, lang, platform, version
  const query = Helpers.serialize({
    'edition': edition ? edition : getServerService().helpEdition(),
    'lang': lang,
    'platform': serverPlatform(),
    'version': version ? version : externalVersion()
  });
  return getHelpUrl('', lang, query, category);
}

function getGeneralSupportUrl(): string {
  if (getServerService().helpEdition() !== 'online') {
    return this.supportUrlPrefix() + '/support';
  }
  // Open a support request (i.e., Zeus support form)
  return 'https://' + getServerService().sessionSupportHost() + getServerService().sessionSupportPath() +
    '?' + Helpers.serialize({
      'uid': getServerService().sessionAuthUserId(),
      'serverSiteUserId': getServerService().sessionUserId(),
      'serverSiteId': getServerService().sessionSiteLuid(),
      'serverInstanceId': getServerService().sessionServerId()
    });
}

function getQueryParams(lang?: string): string {
  return Helpers.serialize({
    'edition': getServerService().helpEdition(),
    'lang': lang || helpUrlLanguage(),
    'platform': serverPlatform(),
    'version': externalVersion()
  });
}

function helpUrlLanguage(): string {
  const language = getServerService().sessionLanguage();
  switch (language) {
    case 'de_DE':
    case 'en_GB':
    case 'en_US':
    case 'es_ES':
    case 'fr_FR':
    case 'it_IT':
    case 'ja_JP':
    case 'ko_KR':
    case 'pt_BR':
    case 'zh_CN':
    case 'zh_TW':
      return language.toLowerCase().replace('_', '-');

    case 'de': return 'de-de';
    case 'en': return 'en-us';
    case 'es': return 'es-es';
    case 'fr': return 'fr-fr';
    case 'it': return 'it-it';
    case 'ja': return 'ja-jp';
    case 'ko': return 'ko-kr';
    case 'pt': return 'pt-br';
    case 'zh': return 'zh-cn';
    default: return 'en-us';
  }
}

// Helper Functions

function getServerService() {
  return ServiceLocator.get<ServerService>('ServerService');
}

function offlineHelpRoute(page?: string): string {
  const lang = helpUrlLanguage().replace('-', '_');
  return 'offline_help/' + lang + '/help.htm' + (page ? '#' + page : '');
}

  // Example: https://www.tableau.com/en-us/app/server-views1?edition=server&lang=en-us&platform=windows&version=9.0
function getHelpUrl(categoryPrefix: string, lang: string, query: string, category?: string): string {
  return `${TABLEAU_URL}/${lang}/app/${categoryPrefix}${(category ? category : 'help1')}?${query}`;
}

function supportUrlPrefix(): string {
  const lang = helpUrlLanguage();
  return TABLEAU_URL + (lang === 'en-us' ? '' : '/' + lang);
}

function serverPlatform(): string {
  const server = getServerService().sessionServerVersion();
  const platform = server ? server.platform : '';
  return (platform) ? platform.toLowerCase() : 'windows'; // Default to Windows
}

function externalVersion(): string {
  const serverVersionInfo = getServerService().sessionServerVersion();
  if (!serverVersionInfo) {
    return '0.0';
  }
  const version = serverVersionInfo.externalVersion;
  const intRegExp = /^\d+$/;
  if (intRegExp.test(version.major) && intRegExp.test(version.minor)) {
    return version.major + '.' + version.minor;
  } else {
    return '0.0';
  }
}

export const HelpService = {
  search,
  getServerHelpUrlByCategory,
  getHelpUrlByCategory,
  supportUrlPrefix,
  getGeneralSupportUrl,
  externalVersion,
  getQueryParams,
  helpUrlLanguage
};

export type HelpService = typeof HelpService;

